import { MessageDescriptor } from "react-intl";

import DealStageType from "@mapmycustomers/shared/enum/DealStageType";
import EntityType from "@mapmycustomers/shared/enum/EntityType";
import {
  ChatChangeDealStageAction,
  ChatCreatableEntityType,
  ChatCreateRecordAction,
  ChatProposalAction,
  UNKNOWN_PROPOSAL_ACTION,
} from "@mapmycustomers/shared/types/chat/ChatContent";
import Deal from "@mapmycustomers/shared/types/entity/Deal";
import Funnel from "@mapmycustomers/shared/types/entity/deals/Funnel";
import Stage from "@mapmycustomers/shared/types/entity/deals/Stage";
import FormLayout from "@mapmycustomers/shared/types/layout/FormLayout";
import { isRecord } from "@mapmycustomers/shared/util/chat/chatWireGuards";

import { blockedMessages } from "@app/component/chat/Conversation/messages/ProposalsMessage/messages";
import { getLossFieldRequirements } from "@app/component/chat/Conversation/messages/ProposalsMessage/rows/lossFieldRequirements";
import {
  moveIntoReadOnlyStageMessage,
  newDealLockedMessage,
} from "@app/util/deal/dealLockMessages";
import { getCreatableFunnels, getStagesFunnel } from "@app/util/deal/dealLockPickers";
import { LockableDeal } from "@app/util/deal/getDealLock";
import getLayoutModelByEntityType from "@app/util/layout/impl";
import loggingService from "@app/util/logging";

import { CREATE_RECORD_LAYOUT_ID_KEY, resolveCreateRecordEntityType } from "./createRecordDraft";
import {
  readCreateRecordValue,
  readIdOf,
  resolveCreateRecordMissingRequired,
  resolveCreateRecordSchema,
  resolveCreateRecordUnjudgeableRequired,
} from "./createRecordSchema";
import { resolveDealFunnelStageDefaults } from "./createRecordSeeds";
import resolveCreateRouteBlockedReason, {
  resolveCreateRouteBlockChangeAt,
} from "./createRouteBlocked";
import { resolveChangeDealStageLock, resolveRelationshipDealLock } from "./proposalDealLocks";
import { resolveChangeDealStageTarget } from "./proposalStage";
import resolveRecordUpdateDealLock from "./recordUpdateDealLock";
import { readRouteStopPicks } from "./routeStopPickDraft";

// Replaces onValidityChange (standalone-card-only, so an empty bundled note was confirmable). Lives
// here, not on the registry, since the apply saga reads it and must never import row components.

const readCreateRecordLayoutId = (
  draft: Record<string, unknown> | undefined
): FormLayout["id"] | undefined => {
  const layoutId = draft?.[CREATE_RECORD_LAYOUT_ID_KEY];
  return typeof layoutId === "number" ? layoutId : undefined;
};

// A create_record draft's field values live under `values`, never at the top level — the other
// two keys there are apply-call instructions, not record data. See createRecordDraft.ts.
const readCreateRecordDraftValues = (
  draft: Record<string, unknown> | undefined
): Record<string, unknown> | undefined => (isRecord(draft?.values) ? draft.values : undefined);

const isCreateRecordFieldMissing = (
  action: ChatCreateRecordAction,
  draft: Record<string, unknown> | undefined,
  entityType: ChatCreatableEntityType,
  funnelStages: Record<Funnel["id"], Stage[]>
): boolean => {
  // The draft wins wholesale once it exists rather than per key: it carries every form field, so
  // falling through per key would resurrect a value the rep cleared and report it as still filled.
  const values = readCreateRecordDraftValues(draft) ?? action.proposedValues;
  const layoutId = readCreateRecordLayoutId(draft);
  const schema = resolveCreateRecordSchema(entityType, values, layoutId, funnelStages);
  if (!schema) {
    // Layouts failed to load: we cannot claim a field is required, so the proposal stays
    // confirmable and the platform's `invalid` outcome is the backstop.
    return false;
  }
  const missing = resolveCreateRecordMissingRequired(schema, values);
  const skipped = resolveCreateRecordUnjudgeableRequired(schema);
  if (skipped.length) {
    loggingService.debug("create_record: required layout entries that cannot be judged", {
      entityType,
      skipped: skipped.map(({ field, key, valued }) => ({
        // The field's own platform name when it resolved, else the raw layout key — which is what
        // `key` falls back to precisely when it did not.
        layoutKey: field?.platformName ?? key,
        reason: valued ? "no field in this build's model" : "layout furniture, holds no value",
      })),
    });
  }
  if (missing.length) {
    // A disabled Create button with a generic tooltip is undebuggable from the outside; this names
    // the offending fields, the layout they came from, and what was actually read for each.
    loggingService.debug("create_record blocked: layout-required fields are still empty", {
      entityType,
      layoutId: layoutId ?? "org default",
      missing: missing.map((schemaField) => ({
        label: schemaField.field?.displayName,
        layoutKey: schemaField.field?.platformName ?? schemaField.key,
        value: readCreateRecordValue(values, schemaField),
        valueKey: schemaField.key,
      })),
    });
  }
  return missing.length > 0;
};

// A new deal can't start in a locked funnel, a funnel whose stages are all read-only, or a proposed
// read-only stage. With no funnel proposed, the raw layout default is judged: the deal lands there.
const isNewDealLocked = (
  values: Record<string, unknown>,
  layoutId: FormLayout["id"] | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>
): boolean => {
  const layoutModel = getLayoutModelByEntityType(EntityType.DEAL);
  const defaults = resolveDealFunnelStageDefaults(
    layoutId !== undefined ? layoutModel.getById(layoutId) : layoutModel.defaultFormLayout,
    funnelStages
  );
  const funnelId = readIdOf(values.funnel) ?? defaults.funnelId;
  if (funnelId === undefined) {
    return false;
  }
  const stageId = readIdOf(values.stage);
  return (
    !getCreatableFunnels([getStagesFunnel(funnelStages, funnelId)], funnelStages).length ||
    !!funnelStages[funnelId]?.find(({ id }) => id === stageId)?.dealsReadOnly
  );
};

const isChangeDealStageBlocked = (
  action: ChatChangeDealStageAction,
  draft: Record<string, unknown> | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>
): boolean => {
  const stage = resolveChangeDealStageTarget(action, draft, funnelStages);
  // A stage the funnel no longer holds is a component concern (the card offers a picker), and an
  // unresolved stage must fail open rather than permanently block confirmation.
  if (stage?.type !== DealStageType.LOST) {
    return false;
  }

  const { commentRequired, reasonRequired } = getLossFieldRequirements(
    getLayoutModelByEntityType(EntityType.DEAL).layouts,
    action.funnelId,
    stage.id
  );
  // Once a draft exists it is authoritative for every loss key it carries, mirroring
  // ChangeDealStageStandalone — falling through per-key would resurrect a cleared value.
  const lossReasonId = draft
    ? typeof draft.dealLossReasonId === "number"
      ? draft.dealLossReasonId
      : undefined
    : action.lossReason?.id;
  const lossComment = draft
    ? typeof draft.dealLossComment === "string"
      ? draft.dealLossComment
      : ""
    : (action.lossComment ?? "");

  return (reasonRequired && !lossReasonId) || (commentRequired && !lossComment.trim());
};

const resolveProposalBlockedReason = (
  action: ChatProposalAction,
  draft: Record<string, unknown> | undefined,
  funnelStages: Record<Funnel["id"], Stage[]>,
  dealRefs: Record<Deal["id"], LockableDeal> = {}
): MessageDescriptor | undefined => {
  switch (action.type) {
    case "create_record": {
      const entityType = resolveCreateRecordEntityType(action, draft);
      if (!entityType) {
        return blockedMessages.chooseRecordType;
      }
      if (
        entityType === EntityType.DEAL &&
        isNewDealLocked(
          readCreateRecordDraftValues(draft) ?? action.proposedValues,
          readCreateRecordLayoutId(draft),
          funnelStages
        )
      ) {
        return newDealLockedMessage;
      }
      return isCreateRecordFieldMissing(action, draft, entityType, funnelStages)
        ? blockedMessages.addRequiredFields
        : undefined;
    }
    case "create_route":
      return resolveCreateRouteBlockedReason(action, draft);
    case "log_note": {
      const note = typeof draft?.note === "string" ? draft.note : action.body;
      return note.trim() ? undefined : blockedMessages.addRequiredFields;
    }
    case "change_deal_stage": {
      const lockMessage = resolveChangeDealStageLock(action, funnelStages, dealRefs);
      if (lockMessage) {
        return lockMessage;
      }
      if (resolveChangeDealStageTarget(action, draft, funnelStages)?.dealsReadOnly) {
        return moveIntoReadOnlyStageMessage;
      }
      return isChangeDealStageBlocked(action, draft, funnelStages)
        ? blockedMessages.addRequiredFields
        : undefined;
    }
    case "record_update":
      return resolveRecordUpdateDealLock(action, draft, funnelStages, dealRefs);
    case "manage_relationship":
      return resolveRelationshipDealLock(action, draft, funnelStages, dealRefs);
    // Nothing is picked for the rep: the platform refuses a candidate confirm without a target.
    case "manage_route_stops_pick":
      return readRouteStopPicks(action, draft).length ? undefined : blockedMessages.pickRouteStop;
    // Every action type this build cannot model arrives as one sentinel — an unknown type, or a
    // known type whose payload failed shape validation (parseProposals.ts). Blocking it stops the
    // rep ticking a row the apply call could only bounce back as `invalid`. It unblocks itself
    // once the type joins ChatProposalAction and gets a registry entry.
    //
    // Keyed on the sentinel and not on registry membership: this file is imported by the apply
    // saga, which must never reach a component module, and registry.ts imports the whole row tree.
    //
    // Debug level, deliberately: this only reaches a console in development and staging (see
    // util/logging/index.ts), never production or sandbox, so it is a local debugging aid, not a
    // way to answer a support question about a live blocked row.
    case UNKNOWN_PROPOSAL_ACTION:
      loggingService.debug("Blocked a proposal this build cannot read", {
        rawType: action.rawType,
      });
      return blockedMessages.unsupportedAction;
    default:
      // every other type has no validity concept at all
      return undefined;
  }
};

// When a row's blocked reason can next change with no input from the rep, so the card re-renders
// then rather than on its minute tick: the apply drops a blocked row without a word. Undefined for
// a type whose reason only changes when its draft does.
export const resolveProposalBlockChangeAt = (
  action: ChatProposalAction,
  draft: Record<string, unknown> | undefined
): number | undefined =>
  action.type === "create_route" ? resolveCreateRouteBlockChangeAt(action, draft) : undefined;

export default resolveProposalBlockedReason;
